import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import metricTotalIcon from "../assets/figma-documents/metric-total.svg";
import metricPendingIcon from "../assets/figma-documents/metric-pending.svg";
import metricFixIcon from "../assets/figma-documents/metric-fix.svg";
import metricApprovedIcon from "../assets/figma-documents/metric-approved.svg";
import trashIcon from "../assets/figma-documents/trash.svg";
import { useAuth } from "../auth/OAuthContext";
import { apiClient, uploadToPresignedUrl } from "../api/client";
import { OrderFlowShell } from "../components/OrderFlowShell";
import { OrderTypeSelect } from "../components/OrderTypeSelect";
import { useDocumentRealtimeInvalidation } from "../hooks/useDocumentRealtime";
import { DocumentRow, OrderTypeRow } from "../types";

export function NurseDashboard() {
  const { idToken } = useAuth();
  const [patientRef, setPatientRef] = useState("");
  const [recipientOrganizationNpi, setRecipientOrganizationNpi] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [orderTypes, setOrderTypes] = useState<OrderTypeRow[]>([]);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [orderTypeId, setOrderTypeId] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const refreshDocuments = useCallback(async () => {
    if (!idToken) return;
    try {
      const { documents } = await apiClient(idToken).getQueue("all");
      setDocuments(documents);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to load sent documents");
    }
  }, [idToken]);

  useEffect(() => {
    if (!idToken) return;
    const client = apiClient(idToken);
    client
      .listOrderTypes()
      .then((res) => {
        setOrderTypes(res.orderTypes);
        const firstType = res.orderTypes[0];
        if (firstType) setOrderTypeId((current) => current || firstType.id);
      })
      .catch(() => {
        // Upload still works without a selected order type.
      });
    void refreshDocuments();
  }, [idToken, refreshDocuments]);

  useDocumentRealtimeInvalidation(idToken, () => refreshDocuments());

  const metrics = useMemo(() => {
    const total = documents.length;
    const pending = documents.filter((d) => d.status === "pending").length;
    const fixRequired = documents.filter((d) => d.status === "fix_requested").length;
    const approved = documents.filter((d) => d.status === "signed").length;
    return { total, pending, fixRequired, approved };
  }, [documents]);

  const fixRequiredDocs = useMemo(() => {
    return documents.filter((d) => d.status === "fix_requested");
  }, [documents]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!idToken || !file) return;

    setSubmitting(true);
    setMessage(null);
    try {
      const client = apiClient(idToken);
      const { uploadUrl, documentId } = await client.requestUploadUrl({
        patientRef,
        recipientOrganizationNpi,
        fileName: file.name,
        contentType: file.type || "application/pdf",
        orderTypeId: orderTypeId || undefined,
      });

      await uploadToPresignedUrl(uploadUrl, file);
      await client.completeUpload(documentId);

      setMessage("Document submitted successfully.");
      setPatientRef("");
      setRecipientOrganizationNpi("");
      setFile(null);
      await refreshDocuments();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  function handleFileDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
    }
  }

  return (
    <OrderFlowShell
      title="Dashboard"
      subtitle="Review all documents routed to your physician organization."
      breadcrumb="/ Dashboard"
    >
      {loadError && (
        <div className="mb-6 p-4 rounded-2xl bg-[#F9EEEE] border border-[#D83933]/20 text-[#D83933] text-sm font-semibold">
          {loadError}
        </div>
      )}
      {message && (
        <div
          className={`mb-6 p-4 rounded-2xl text-sm font-semibold border ${
            message.includes("success")
              ? "bg-[#E3F5E1] border-[#008817]/20 text-[#008817]"
              : "bg-[#F9EEEE] border-[#D83933]/20 text-[#D83933]"
          }`}
        >
          {message}
        </div>
      )}

      {/* 4 Overview Statistics Cards (Figma 37:6402) */}
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-8 font-sans" aria-label="Overview statistics">
        <div className="p-4 rounded-2xl bg-[#FDFFFC] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-[#000000]">Total sent</span>
            <div className="w-14 h-14 rounded-full bg-[#F2F4FA] flex items-center justify-center">
              <img src={metricTotalIcon} alt="" aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#000000] font-display">{metrics.total}</div>
        </div>

        <div className="p-4 rounded-2xl bg-[#FDFFFC] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-[#000000]">Pending</span>
            <div className="w-14 h-14 rounded-full bg-[#F2F4FA] flex items-center justify-center">
              <img src={metricPendingIcon} alt="" aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#000000] font-display">{metrics.pending}</div>
        </div>

        <div className="p-4 rounded-2xl bg-[#FDFFFC] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-[#000000]">Fix required</span>
            <div className="w-14 h-14 rounded-full bg-[#F2F4FA] flex items-center justify-center">
              <img src={metricFixIcon} alt="" aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#000000] font-display">{metrics.fixRequired}</div>
        </div>

        <div className="p-4 rounded-2xl bg-[#FDFFFC] shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.12)] flex flex-col justify-between gap-4">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-[#000000]">Approved</span>
            <div className="w-14 h-14 rounded-full bg-[#F2F4FA] flex items-center justify-center">
              <img src={metricApprovedIcon} alt="" aria-hidden="true" />
            </div>
          </div>
          <div className="text-3xl font-bold text-[#000000] font-display">{metrics.approved}</div>
        </div>
      </section>

      {/* Two Column Layout: Send document (Left) & Fix required (Right) - Figma 37:6402 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start font-sans">
        {/* Left Column: Send Document Form (Figma 37:6464) */}
        <div className="bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#011627] font-display">Send document</h2>
            <p className="text-sm text-[#7F8A92] mt-0.5 font-normal">Route by recipient organization NPI</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="patientRef" className="text-sm font-bold text-[#24313D]">
                Patient reference
              </label>
              <input
                id="patientRef"
                type="text"
                className="w-full min-h-[46px] px-4 py-2.5 text-base bg-[#FDFFFC] border border-[#E6E8EA] rounded-xl outline-none focus:border-[#012699] focus:ring-2 focus:ring-[#012699]/15 transition-all text-[#011627] placeholder:text-[#A9AEB1]"
                value={patientRef}
                onChange={(e) => setPatientRef(e.target.value)}
                placeholder="e.g. Jessica Brown"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="orgNpi" className="text-sm font-bold text-[#24313D]">
                Recipient Organization NPI
              </label>
              <input
                id="orgNpi"
                type="text"
                className="w-full min-h-[46px] px-4 py-2.5 text-base bg-[#FDFFFC] border border-[#E6E8EA] rounded-xl outline-none focus:border-[#012699] focus:ring-2 focus:ring-[#012699]/15 transition-all text-[#011627] placeholder:text-[#A9AEB1]"
                value={recipientOrganizationNpi}
                onChange={(e) => setRecipientOrganizationNpi(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="10-Digit NPI"
                pattern="\d{10}"
                title="Must be exactly 10 digits"
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <span id="orderTypeLabel" className="text-sm font-bold text-[#24313D]">
                Order type
              </span>
              <OrderTypeSelect options={orderTypes} value={orderTypeId} onChange={setOrderTypeId} />
            </div>

            {/* Dropzone File Upload (Figma 37:6402) */}
            <div className="flex flex-col gap-3">
              <div
                className={`relative border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
                  isDragOver
                    ? "border-[#012699] bg-[#F2F4FA]"
                    : "border-[#CCD5EC] hover:border-[#012699] bg-[#FAFBFD]"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
              >
                <input
                  type="file"
                  id="nurseFileInput"
                  className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    e.target.value = "";
                  }}
                />
                <div className="flex flex-col items-center gap-1 pointer-events-none">
                  <span className="text-sm text-[#7F8A92]">Click to browse or</span>
                  <span className="text-sm text-[#7F8A92]">drag and drop your files</span>
                </div>
              </div>

              {file && (
                <div className="flex items-center justify-between p-3.5 rounded-xl bg-[#F2F4FA] border border-[#E6E8EA]">
                  <span className="text-sm font-bold text-[#011627] truncate max-w-[280px]">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    className="p-1.5 rounded-lg text-[#7F8A92] hover:text-[#D83933] hover:bg-white transition-colors cursor-pointer"
                    onClick={() => setFile(null)}
                    aria-label="Remove selected file"
                  >
                  <img src={trashIcon} alt="" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="w-full min-h-[38px] flex items-center justify-center font-bold text-base text-white bg-[#012699] hover:bg-[#0B2981] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed rounded-full transition-all cursor-pointer mt-2"
              disabled={submitting || !file}
            >
              {submitting ? "Uploading..." : "Upload for review"}
            </button>
          </form>
        </div>

        {/* Right Column: Fix Required List (Figma 37:6402 / 37:6482) */}
        <div className="bg-[#FDFFFC] rounded-2xl border border-[#E6E8EA] p-4 flex flex-col">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[#011627] font-display">Fix required</h2>
            <p className="text-sm text-[#7F8A92] mt-0.5 font-normal">Route by recipient organization NPI</p>
          </div>

          <div className="flex flex-col gap-4 overflow-y-auto">
            {fixRequiredDocs.length === 0 ? (
              <div className="py-20 text-center text-[#7F8A92] text-base font-medium border border-dashed border-[#E6E8EA] rounded-2xl bg-[#FAFBFD]">
                No documents currently require fixes.
              </div>
            ) : (
              fixRequiredDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="p-4 rounded-2xl bg-white transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-[0px_8px_18px_-6px_rgba(24,39,75,0.16)]"
                >
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <span className="text-sm font-bold text-[#0B2981] w-fit">
                      Fix required
                    </span>
                    <strong className="text-lg font-bold text-[#011627] font-display truncate">
                      {doc.patient_ref || "Untitled Reference"}
                    </strong>
                    <p className="text-sm text-[#7F8A92]">
                      Sent by {doc.doctor_name || doc.doctor_email || "Doctor"}
                      {doc.doctor_npi ? ` (NPI ${doc.doctor_npi})` : ""}
                    </p>
                  </div>
                  <Link
                    to={`/documents/${doc.id}`}
                    className="inline-flex items-center justify-center px-5 py-2 rounded-full text-sm font-bold text-white bg-[#012699] hover:bg-[#0B2981] transition-all shrink-0 self-start sm:self-center"
                  >
                    Take action
                  </Link>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </OrderFlowShell>
  );
}
